import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  View, 
  Text, 
  TouchableOpacity, 
  StyleSheet, 
  ScrollView,
  Platform,
  StatusBar,
  ActivityIndicator,
  Keyboard,
} from 'react-native';
import MentionInput, { MentionSuggestionList, MentionUser } from '@/components/MentionInput';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import { useFonts, Inter_400Regular, Inter_500Medium, Inter_700Bold } from '@expo-google-fonts/inter';
import Avatar from '@/components/Avatar';
import FormattedText from '@/components/FormattedText';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { communityService, CommunityEncouragement } from '@/services/communityService';
import ActionBottomSheet from '@/components/ActionBottomSheet';
import Toast from '@/components/Toast';
import { SkeletonComment } from '@/components/Skeleton';

export default function CommunityPostScreen() {
  const { t } = useLanguage();
  const router = useRouter();
  const params = useLocalSearchParams();
  const { userProfile } = useAuth();
  
  const insets = useSafeAreaInsets();
  const postId = (params.postId || params.id) as string;
  const [postUserId, setPostUserId] = useState(params.postUserId as string || '');
  const [postAuthor, setPostAuthor] = useState(params.author as string || t('community.post.defaultAuthor'));
  const [postAuthorPhoto, setPostAuthorPhoto] = useState(params.authorPhoto as string || null);
  const [postContent, setPostContent] = useState(params.content as string || '');
  const [postTime, setPostTime] = useState(params.time as string || '');
  const [isVerified, setIsVerified] = useState(params.verified === 'true');
  
  const isOwnPost = userProfile?.id === postUserId;
  
  const isDeepLink = !params.postId && !!params.id;
  
  const [isSupported, setIsSupported] = useState(params.isSupported === 'true');
  const [supportCount, setSupportCount] = useState(parseInt(params.supportCount as string || '0', 10));
  const [encourageCount, setEncourageCount] = useState(parseInt(params.encourageCount as string || '0', 10));
  const [encouragements, setEncouragements] = useState<CommunityEncouragement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [encouragementText, setEncouragementText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState<MentionUser[]>([]);
  const [mentionIsSearching, setMentionIsSearching] = useState(false);
  const [mentionSelectUser, setMentionSelectUser] = useState<((user: MentionUser) => void) | null>(null);
  const [inputSectionHeight, setInputSectionHeight] = useState(64);
  const [actionMenuVisible, setActionMenuVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const scrollViewRef = useRef<ScrollView>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const handlePostMenuPress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setActionMenuVisible(true);
  };

  const handleDeletePost = async () => {
    try {
      const success = await communityService.deletePost(postId);
      if (success) {
        showToast(t('community.post.postRemoved'), 'success');
        setTimeout(() => {
          router.back();
        }, 500);
      } else {
        showToast(t('community.post.postRemoveError'), 'error');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      showToast(t('community.post.postRemoveError'), 'error');
    }
    
    setActionMenuVisible(false);
  };

  const handleEditPost = () => {
    setActionMenuVisible(false);
    
    router.push({
      pathname: '/community-new-post',
      params: {
        editMode: 'true',
        postId: postId,
        existingContent: postContent,
        existingTopics: JSON.stringify([]),
      },
    });
  };


  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_700Bold,
  });

  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const showSub = Keyboard.addListener(showEvent, (e) => setKeyboardHeight(e.endCoordinates.height));
    const hideSub = Keyboard.addListener(hideEvent, () => setKeyboardHeight(0));
    return () => { showSub.remove(); hideSub.remove(); };
  }, []);

  useEffect(() => {
    // PRD §5.7 — `community_post_viewed`. Fires once per mount with the
    // post id from the route params; Clarity dedupes per session so re-
    // visits inside the same session aren't double-counted.
    if (postId) {
      import('@/utils/clarityEvents')
        .then(({ logCommunityPostViewed }) =>
          logCommunityPostViewed({ post_id: String(postId) })
        )
        .catch(() => {});
    }
  }, [postId]);

  const formatTimestampShort = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return t('community.post.timestampNow');
    if (diffMins < 60) return `${diffMins}min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
  };

  const loadPost = useCallback(async () => {
    if (!postId) {
      setIsLoading(false);
      return;
    }
    
    try {
      const post = await communityService.getPostById(postId);
      if (post) {
        setIsSupported(post.user_has_supported);
        setSupportCount(post.support_count);
        setEncourageCount(post.encourage_count);
        if (post.encouragements) {
          setEncouragements(post.encouragements);
        }
        if (isDeepLink) {
          setPostUserId(post.user_id);
          setPostAuthor(post.author?.full_name || t('community.post.defaultAuthor'));
          setPostAuthorPhoto(post.author?.avatar_url || null);
          setPostContent(post.content);
          setPostTime(formatTimestampShort(post.created_at));
          setIsVerified(post.author?.is_admin || false);
        }
      }
    } catch (error) {
      console.error('Error loading post:', error);
    } finally {
      setIsLoading(false);
    }
  }, [postId, isDeepLink]);

  useFocusEffect(
    useCallback(() => {
      loadPost();
    }, [loadPost])
  );

  if (!fontsLoaded) {
    return null;
  }

  const handleBack = () => {
    router.back();
  };

  const handleSupport = async () => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    
    const previousSupported = isSupported;
    const previousCount = supportCount;
    
    setIsSupported(!isSupported);
    setSupportCount(isSupported ? Math.max(0, supportCount - 1) : supportCount + 1);
    
    try {
      const result = await communityService.toggleSupport(postId, previousSupported);
      setSupportCount(result.support_count);
      setIsSupported(result.user_has_supported);
    } catch (error) {
      console.error('Error toggling support:', error);
      setIsSupported(previousSupported);
      setSupportCount(previousCount);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSubmitEncouragement = async () => {
    if (!encouragementText.trim() || isSubmitting || !postId) return;
    
    setIsSubmitting(true);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    
    try {
      const result = await communityService.addEncouragement(postId, encouragementText.trim());
      if (result.encouragement) {
        setEncouragements(prev => [result.encouragement!, ...prev]);
      }
      setEncourageCount(result.encourage_count);
      setEncouragementText('');
      // PRD §5.7 — `community_comment_submitted`. "Encouragement" is the
      // product term for the comment primitive on community posts.
      import('@/utils/clarityEvents')
        .then(({ logCommunityCommentSubmitted }) =>
          logCommunityCommentSubmitted({ post_id: String(postId) })
        )
        .catch(() => {});
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (error) {
      console.error('Error adding encouragement:', error);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAvatarPress = (userId: string, userName: string, userPhoto: string | null) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push({
      pathname: '/community-profile',
      params: {
        userId: userId,
        name: userName,
        verified: 'false',
      },
    });
  };


  const EncouragementCard = ({ encouragement }: { encouragement: CommunityEncouragement }) => {
    const authorName = encouragement.author?.full_name || t('community.post.anonymous');
    const authorPhoto = encouragement.author?.avatar_url;
    const authorId = encouragement.user_id;
    
    return (
      <View style={styles.commentCard}>
        <TouchableOpacity onPress={() => handleAvatarPress(authorId, authorName, authorPhoto || null)}>
          <Avatar
            uri={authorPhoto || null}
            size={32}
            showInitials={true}
            initials={authorName}
            loading={false}
          />
        </TouchableOpacity>
        <View style={styles.commentContent}>
          <Text style={styles.commentText}>
            <Text style={styles.commentAuthor}>{authorName} </Text>
            <FormattedText content={encouragement.content} style={styles.commentText} />
          </Text>
        </View>
        <Text style={styles.commentTime}>{formatTimestampShort(encouragement.created_at)}</Text>
      </View>
    );
  };

  return (
    <>
      <StatusBar barStyle="dark-content" backgroundColor="#FFFFFF" />
      <SafeAreaView style={[styles.container, { paddingBottom: keyboardHeight }]} edges={['top']}>
        <>
          {/* Header */}
          <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path 
                d="M16 19L9 12L16 5" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="square" 
              />
            </Svg>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('community.post.headerTitle')}</Text>
          <View style={styles.headerPlaceholder} />
        </View>

        <ScrollView 
          ref={scrollViewRef}
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {/* Post Section */}
          <View style={styles.postSection}>
            <TouchableOpacity onPress={() => handleAvatarPress(postUserId, postAuthor, postAuthorPhoto)} style={styles.avatarColumn}>
              <Avatar
                uri={postAuthorPhoto}
                size={32}
                showInitials={true}
                initials={postAuthor}
                loading={false}
              />
            </TouchableOpacity>
            <View style={styles.postContentColumn}>
              <View style={styles.postHeader}>
                <View style={styles.nameWithBadge}>
                  <Text style={styles.postAuthor}>{postAuthor}</Text>
                  {isVerified && (
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 4 }}>
                      <Path d="M8 0L9.79611 2.02786L12.4721 1.52786L12.9721 4.20389L15.5 5.5L14.5 8L15.5 10.5L12.9721 11.7961L12.4721 14.4721L9.79611 13.9721L8 16L6.20389 13.9721L3.52786 14.4721L3.02786 11.7961L0.5 10.5L1.5 8L0.5 5.5L3.02786 4.20389L3.52786 1.52786L6.20389 2.02786L8 0Z" fill="#6B9EC2"/>
                      <Path d="M5.5 8L7 9.5L10.5 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </Svg>
                  )}
                </View>
                <View style={styles.headerRight}>
                  <Text style={styles.postTime}>{postTime}</Text>
                  {isOwnPost && (
                    <TouchableOpacity 
                      style={styles.postMenuButton}
                      onPress={handlePostMenuPress}
                    >
                      <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <Path d="M10 5.5C10.8284 5.5 11.5 4.82843 11.5 4C11.5 3.17157 10.8284 2.5 10 2.5C9.17157 2.5 8.5 3.17157 8.5 4C8.5 4.82843 9.17157 5.5 10 5.5Z" fill="#757575"/>
                        <Path d="M10 11.5C10.8284 11.5 11.5 10.8284 11.5 10C11.5 9.17157 10.8284 8.5 10 8.5C9.17157 8.5 8.5 9.17157 8.5 10C8.5 10.8284 9.17157 11.5 10 11.5Z" fill="#757575"/>
                        <Path d="M10 17.5C10.8284 17.5 11.5 16.8284 11.5 16C11.5 15.1716 10.8284 14.5 10 14.5C9.17157 14.5 8.5 15.1716 8.5 16C8.5 16.8284 9.17157 17.5 10 17.5Z" fill="#757575"/>
                      </Svg>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
              
              <FormattedText 
                content={postContent}
                style={styles.postContent}
              />

              <View style={styles.postEngagement}>
                <TouchableOpacity style={styles.engagementItem} onPress={handleSupport} disabled={isUpdating}>
                  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <Path 
                      d="M12.0017 5.92507C11.3979 5.24903 10.6225 4.72728 9.76648 4.42682C6.4652 3.25658 3.38047 5.59684 3.03157 8.87727C2.5431 13.8208 7.83511 16.8611 11.1366 19.572C11.5082 19.8819 12.0512 19.967 12.4989 19.7815C12.7138 19.7 12.8866 19.5452 13.0616 19.4014C16.3914 16.6725 21.6522 13.5358 20.9334 8.55669C20.401 5.07868 16.7703 3.12597 13.6389 4.67763C13.0203 4.98409 12.4606 5.40932 12.0017 5.92507Z" 
                      fill={isSupported ? "#DE5609" : "none"}
                      stroke={isSupported ? "#DE5609" : "#757575"}
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </Svg>
                  <Text style={styles.engagementCount}>{supportCount}</Text>
                </TouchableOpacity>
                <View style={styles.engagementItem}>
                  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <Path 
                      fillRule="evenodd" 
                      clipRule="evenodd" 
                      d="M9.44015 18.0968L10.8048 19.4662C11.3597 20.0229 12.2573 20.0229 12.8112 19.4662L14.1767 18.0968C14.5653 17.707 15.0921 17.4886 15.6414 17.4886H16.6669C18.7555 17.4886 20.448 15.7895 20.448 13.6938V7.03212C20.448 4.93651 18.7555 3.23828 16.6669 3.23828H6.94996C4.86141 3.23828 3.16797 4.93651 3.16797 7.03212V13.6938C3.16797 15.7895 4.86141 17.4886 6.94996 17.4886H7.97554C8.52477 17.4886 9.05158 17.707 9.44015 18.0968Z" 
                      stroke="#757575" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </Svg>
                  <Text style={styles.engagementCount}>{encourageCount}</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Encouragements Section */}
          <View style={styles.commentsSection}>
            {isLoading ? (
              <>
                <SkeletonComment />
                <SkeletonComment />
                <SkeletonComment />
              </>
            ) : encouragements.length === 0 ? (
              <View style={styles.emptyEncouragements}>
                <Text style={styles.emptyText}>{t('community.post.firstToEncourage')}</Text>
              </View>
            ) : (
              // P04 audit: FlatList not applied here. The encouragements list
              // sits inside a ScrollView that also contains the full post body
              // above it. Nesting FlatList inside ScrollView would disable
              // FlatList's virtualization (RN VirtualizedList constraint).
              // Proper virtualization requires converting the whole screen to a
              // FlatList with the post as ListHeaderComponent — a larger
              // refactor. Encouragement counts are bounded in practice (<50),
              // so the current approach is acceptable. Revisit as P04c if
              // high-engagement posts regularly exceed 100 encouragements.
              encouragements.map((encouragement) => (
                <EncouragementCard 
                  key={encouragement.id} 
                  encouragement={encouragement}
                />
              ))
            )}
          </View>
        </ScrollView>

        {/* Floating mention suggestions — absolutely positioned above input section */}
        {(mentionSuggestions.length > 0 || mentionIsSearching) && (
          <View style={[
            styles.floatingSuggestions,
            { bottom: keyboardHeight + inputSectionHeight + 12 },
          ]}>
            <MentionSuggestionList
              suggestions={mentionSuggestions}
              isSearching={mentionIsSearching}
              onSelect={(user) => mentionSelectUser?.(user)}
            />
          </View>
        )}

        {/* Encouragement Input */}
        <View
          style={[styles.inputSection, { paddingBottom: keyboardHeight > 0 ? 8 : Math.max(insets.bottom, 8) }]}
          onLayout={(e) => setInputSectionHeight(e.nativeEvent.layout.height)}
        >
          <View style={styles.emojiRow}>
            {['💙', '🙌', '🔥', '👏', '😢', '😍', '😮', '😂'].map((emoji) => (
              <TouchableOpacity
                key={emoji}
                style={styles.emojiButton}
                onPress={() => setEncouragementText((prev) => prev + emoji)}
                activeOpacity={0.6}
              >
                <Text style={styles.emojiText}>{emoji}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={styles.inputRow}>
          <Avatar
            uri={userProfile?.avatar_url || null}
            size={40}
            showInitials={true}
            initials={userProfile?.full_name || t('community.author.default')}
            loading={false}
          />
          <View style={styles.inputFieldContainer}>
            <MentionInput
              style={styles.textInput}
              placeholder={t('community.post.commentPlaceholder')}
              placeholderTextColor="#A19F97"
              value={encouragementText}
              onChangeText={setEncouragementText}
              multiline
              editable={!isSubmitting}
              externalDropdown
              onSuggestionsChange={(suggestions, isSearching, onSelect) => {
                setMentionSuggestions(suggestions);
                setMentionIsSearching(isSearching);
                setMentionSelectUser(() => onSelect);
              }}
              onFocus={() => setTimeout(() => scrollViewRef.current?.scrollToEnd({ animated: true }), 300)}
            />
            {encouragementText.trim().length > 0 && (
              <TouchableOpacity 
                style={styles.sendButton}
                onPress={handleSubmitEncouragement}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <Path 
                      d="M8.16602 3.3999V13" 
                      stroke="white" 
                      strokeWidth="1.5" 
                      strokeLinecap="square" 
                    />
                    <Path 
                      d="M12.183 7.03331L8.16704 3L4.15039 7.03331" 
                      stroke="white" 
                      strokeWidth="1.5" 
                      strokeLinecap="square" 
                    />
                  </Svg>
                )}
              </TouchableOpacity>
            )}
          </View>
          </View>
        </View>
        </>

        {/* Action Menu Bottom Sheet */}
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: actionMenuVisible ? 'auto' : 'none' }}>
          <ActionBottomSheet
            isVisible={actionMenuVisible}
            onClose={() => setActionMenuVisible(false)}
            title={t('community.post.options')}
          >
            <TouchableOpacity 
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                padding: 16,
                backgroundColor: '#F9F8F3',
                borderRadius: 12,
                borderWidth: 1,
                borderColor: '#F0EEE4',
                gap: 16,
                marginBottom: 8,
              }} 
              onPress={handleEditPost}
            >
              <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <Path d="M6.77914 16.6022L8.16793 16.1982C8.9144 15.9807 9.56473 15.516 10.0113 14.8807L15.3353 7.49803C15.9726 6.62359 15.7804 5.39908 14.906 4.76171L13.4614 3.70856C12.5871 3.07228 11.3615 3.26471 10.7251 4.13793L5.34262 11.6032C4.93187 12.1882 4.71484 12.8868 4.72082 13.602L4.73417 15.0818C4.74426 16.1408 5.7625 16.8977 6.77914 16.6022Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
              <Text style={styles.actionCardText}>{t('community.post.editPost')}</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                padding: 16,
                backgroundColor: '#F9F8F3',
                borderRadius: 12,
                borderWidth: 1,
                borderColor: '#F0EEE4',
                gap: 16,
              }} 
              onPress={handleDeletePost}
            >
              <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <Path d="M4.10352 8.11298L4.57425 14.5558C4.67154 15.8894 5.78214 16.9221 7.11875 16.9221H11.7289C13.0663 16.9221 14.1762 15.8894 14.2735 14.5565L14.7442 8.11298" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M15.7814 5.6926H3.06641" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M12.3246 5.69328L11.9377 3.78865C11.8255 3.36881 11.4445 3.07694 11.0105 3.07694H7.84031C7.40399 3.07544 7.02157 3.36731 6.90856 3.78865L6.52539 5.69328" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M7.96289 9.8317V13.2997M10.5632 9.8317V13.2997" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
              <Text style={styles.actionCardText}>{t('community.post.deletePost')}</Text>
            </TouchableOpacity>
          </ActionBottomSheet>
        </View>

        {/* Toast */}
        <Toast
          visible={toastVisible}
          message={toastMessage}
          type={toastType}
          onHide={() => setToastVisible(false)}
        />
      </SafeAreaView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardView: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    height: 56,
  },
  backButton: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 14,
  },
  headerPlaceholder: {
    width: 24,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  postSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  avatarColumn: {
    alignItems: 'center',
  },
  postContentColumn: {
    flex: 1,
    flexDirection: 'column',
    gap: 8,
  },
  postHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  nameWithBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  postMenuButton: {
    padding: 4,
  },
  actionCardText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  postAuthor: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 19.6,
  },
  postTime: {
    color: '#7E7D75',
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 12,
  },
  postContent: {
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
  postEngagement: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  engagementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  engagementCount: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#757575',
    lineHeight: 19.6,
  },
  divider: {
    height: 1,
    backgroundColor: '#F0EEE4',
    width: '100%',
  },
  commentsSection: {
    backgroundColor: '#FFFFFF',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  commentCard: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
  },
  commentContent: {
    flex: 1,
  },
  commentAuthor: {
    fontFamily: 'Inter_700Bold',
    fontWeight: '700',
  },
  commentTime: {
    color: '#7E7D75',
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 16.8,
    marginTop: 2,
  },
  commentText: {
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
  inputSection: {
    paddingTop: 6,
    paddingBottom: 8,
    paddingHorizontal: 24,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#F0EEE4',
    flexDirection: 'column',
  },
  floatingSuggestions: {
    position: 'absolute',
    left: 24,
    right: 24,
    zIndex: 200,
    elevation: 200,
  },
  emojiRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 8,
  },
  emojiButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 4,
  },
  emojiText: {
    fontSize: 24,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  inputFieldContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#F9F8F3',
    minHeight: 40,
    gap: 8,
  },
  textInput: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    maxHeight: 80,
  },
  sendButton: {
    width: 32,
    height: 32,
    borderRadius: 27,
    backgroundColor: '#151515',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyEncouragements: {
    padding: 24,
    alignItems: 'center',
  },
  emptyText: {
    color: '#7E7D75',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
  },
});
